import { useEffect, useState } from "react";

/**
 * Sidebar Component - Filter controls and search functionality
 * @param {Object} props
 * @param {string} props.searchQuery - Current search query
 * @param {Function} props.onSearchChange - Handler for search query changes
 * @param {Array<string>} props.selectedIndustries - Selected industry filters
 * @param {Array<string>} props.availableIndustries - Available industry options
 * @param {Function} props.onToggleIndustry - Handler for toggling industry filters
 * @param {Array<string>} props.selectedTypes - Selected type filters
 * @param {Array<string>} props.availableTypes - Available type options
 * @param {Function} props.onToggleType - Handler for toggling type filters
 * @param {Object} props.parameterRange - Parameter count range {min, max}
 * @param {Function} props.onParameterRangeChange - Handler for parameter range changes
 * @param {Array<string>} props.selectedAssets - Currently selected asset IDs
 * @param {Array<Object>} props.availableAssets - Available assets with id and name
 * @param {Function} props.onToggleAsset - Handler for toggling asset filters
 * @param {Function} props.onAssetDetails - Handler to view asset details/endpoints
 * @param {Array<Object>} props.assetsData - Full asset data for details
 * @param {Function} props.onClearFilters - Handler to clear all filters
 * @param {number} props.filteredCount - Number of filtered equipment items
 */
function Sidebar({
  searchQuery,
  onSearchChange,
  selectedIndustries,
  availableIndustries,
  onToggleIndustry,
  selectedTypes,
  availableTypes,
  onToggleType,
  parameterRange,
  onParameterRangeChange,
  selectedAssets,
  availableAssets,
  onToggleAsset,
  onAssetDetails,
  assetsData,
  onClearFilters,
  filteredCount,
}) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const hasActiveFilters =
    selectedTypes.length > 0 ||
    selectedIndustries.length > 0 ||
    searchQuery ||
    parameterRange.min !== 0 ||
    parameterRange.max !== 10 ||
    selectedAssets.length > 0;

  // Lock body scroll when filters are open on mobile
  useEffect(() => {
    if (isFilterOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflowY = "hidden";
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflowY = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflowY = "";
    };
  }, [isFilterOpen]);

  // Auto-close filter when resizing from mobile to desktop view
  useEffect(() => {
    const handleResize = () => {
      // Close filter if window width exceeds mobile breakpoint (992px)
      if (window.innerWidth > 992 && isFilterOpen) {
        setIsFilterOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isFilterOpen]);

  const handleApplyFilters = () => {
    setIsFilterOpen(false);
  };

  const handleToggleFilters = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  return (
    <aside className="sidebar">
      {/* Mobile Filter Toggle Button */}
      <button
        className="mobile-filter-toggle"
        onClick={handleToggleFilters}
        aria-label="Toggle filters"
      >
        <span className="filter-text">Filters</span>
        {hasActiveFilters && (
          <span className="filter-badge">
            {[
              selectedTypes.length,
              selectedIndustries.length,
              selectedAssets.length,
              searchQuery ? 1 : 0,
              parameterRange.min !== 0 || parameterRange.max !== 10 ? 1 : 0,
            ].reduce((a, b) => a + b, 0)}
          </span>
        )}
        <span className={`filter-arrow ${isFilterOpen ? "open" : ""}`}>▼</span>
      </button>

      {/* Filter Content - slides down on mobile */}
      <div className={`sidebar-content ${isFilterOpen ? "open" : ""}`}>
        <div className="sidebar-intro-wrapper">
          {/* Welcome Message */}
          <div className="sidebar-welcome">
            <h3 className="welcome-title">Asset Simulator</h3>
            <p className="welcome-text">
              Explore and monitor industrial equipment across multiple assets.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="sidebar-disclaimer">
            <div className="disclaimer-icon">⚠️</div>
            <p className="disclaimer-text">
              <strong>Simulation Only:</strong> This platform is for simulation
              and demonstration purposes. Data displayed may not be technically
              accurate or represent real-world conditions.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="filter-section search-section">
          <input
            type="text"
            className="search-input"
            placeholder="Search equipment..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => onSearchChange("")}>
              ×
            </button>
          )}
        </div>

        {/* Asset Filter */}
        <div className="filter-section">
          <div
            className="filter-header"
            onClick={(e) =>
              e.currentTarget.parentElement.classList.toggle("collapsed")
            }
          >
            <span className="filter-title">Assets</span>
            <span className="filter-toggle">−</span>
          </div>
          <div className="filter-content">
            {availableAssets.map((asset) => (
              <div key={asset.id} className="filter-checkbox-with-action">
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedAssets.includes(asset.id)}
                    onChange={() => onToggleAsset(asset.id)}
                  />
                  <span className="checkbox-label">{asset.name}</span>
                </label>
                <button
                  className="asset-info-btn"
                  onClick={() => {
                    const fullAsset = assetsData.find((a) => a.id === asset.id);
                    if (fullAsset) onAssetDetails(fullAsset);
                  }}
                  title="View Endpoints"
                >
                  ⓘ
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Industry Filter */}
        <div className="filter-section">
          <div
            className="filter-header"
            onClick={(e) =>
              e.currentTarget.parentElement.classList.toggle("collapsed")
            }
          >
            <span className="filter-title">Industry</span>
            <span className="filter-toggle">−</span>
          </div>
          <div className="filter-content">
            {availableIndustries.map((industry) => (
              <label key={industry} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedIndustries.includes(industry)}
                  onChange={() => onToggleIndustry(industry)}
                />
                <span className="checkbox-label">{industry}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Equipment Type Filter */}
        <div className="filter-section">
          <div
            className="filter-header"
            onClick={(e) =>
              e.currentTarget.parentElement.classList.toggle("collapsed")
            }
          >
            <span className="filter-title">Equipment Type</span>
            <span className="filter-toggle">−</span>
          </div>
          <div className="filter-content">
            {availableTypes.map((type) => (
              <label key={type} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedTypes.includes(type)}
                  onChange={() => onToggleType(type)}
                />
                <span className="checkbox-label">{type}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Parameter Count Filter */}
        <div className="filter-section">
          <div
            className="filter-header"
            onClick={(e) =>
              e.currentTarget.parentElement.classList.toggle("collapsed")
            }
          >
            <span className="filter-title">Parameter Count</span>
            <span className="filter-toggle">−</span>
          </div>
          <div className="filter-content">
            <div className="range-inputs">
              <div className="range-input-group">
                <label>Min:</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={parameterRange.min}
                  onChange={(e) =>
                    onParameterRangeChange({
                      ...parameterRange,
                      min: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="range-input-group">
                <label>Max:</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={parameterRange.max}
                  onChange={(e) =>
                    onParameterRangeChange({
                      ...parameterRange,
                      max: parseInt(e.target.value) || 10,
                    })
                  }
                />
              </div>
            </div>
            <div className="range-slider">
              <input
                type="range"
                min="0"
                max="10"
                value={parameterRange.max}
                onChange={(e) =>
                  onParameterRangeChange({
                    ...parameterRange,
                    max: parseInt(e.target.value),
                  })
                }
              />
            </div>
          </div>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button className="clear-filters-btn" onClick={onClearFilters}>
            Clear All Filters
          </button>
        )}

        {/* Equipment Count */}
        <div className="equipment-count">
          Found equipment: <strong>{filteredCount}</strong>
        </div>

        {/* Mobile Apply Button */}
        <button className="mobile-apply-filters" onClick={handleApplyFilters}>
          Apply
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
